import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Crown, CalendarClock, RotateCcw } from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch { return "—"; }
}

export default function ManageSubscription({ usage, onChanged }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const canceling = usage?.cancel_at_period_end;
  const trialing = usage?.subscription_status === "trialing";

  const cancel = async () => {
    setLoading(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Subscription will end at your billing period", { description: "You keep Pro access until then." });
      await onChanged();
    } catch (e) {
      toast.error("Could not cancel", { description: e.response?.data?.detail || "Try again." });
    } finally { setLoading(false); }
  };

  const resume = async () => {
    setLoading(true);
    try {
      await api.post("/subscription/resume");
      toast.success("Subscription resumed — you're all set!");
      await onChanged();
    } catch (e) {
      toast.error("Could not resume", { description: e.response?.data?.detail || "Try again." });
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="manage-subscription-button" className="hidden sm:inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors">
          <Crown className="h-3.5 w-3.5" /> PRO
        </button>
      </DialogTrigger>
      <DialogContent data-testid="manage-subscription-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Crown className="h-5 w-5 text-primary" /> Receipt Republic Pro
          </DialogTitle>
          <DialogDescription>
            Unlimited receipts · $6/month · billed automatically each month.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-border p-4 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Status</span>
            <span data-testid="subscription-status" className="font-semibold capitalize">
              {canceling ? "Canceling" : trialing ? "Free trial" : (usage?.subscription_status || "active")}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground flex items-center gap-1.5">
              <CalendarClock className="h-4 w-4" /> {canceling ? "Access ends" : trialing ? "Trial ends" : "Renews on"}
            </span>
            <span data-testid="subscription-renewal" className="font-semibold">{fmtDate(usage?.current_period_end)}</span>
          </div>
        </div>

        {canceling && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            Your subscription is set to cancel. You'll keep Pro until the date above.
          </p>
        )}

        <DialogFooter>
          {canceling ? (
            <Button data-testid="resume-subscription-button" onClick={resume} disabled={loading} className="w-full rounded-xl bg-primary hover:bg-primary/90">
              <RotateCcw className="h-4 w-4 mr-2" /> {loading ? "Resuming…" : "Resume subscription"}
            </Button>
          ) : (
            <Button data-testid="cancel-subscription-button" onClick={cancel} disabled={loading} variant="outline" className="w-full rounded-xl border-destructive/30 text-destructive hover:bg-red-50">
              {loading ? "Canceling…" : "Cancel subscription"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
